import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
  useTheme,
} from '@mui/material';
import { 
  Add as AddIcon,
} from '@mui/icons-material';
import NewProjectModal, { ProjectFormData } from '../components/NewProjectModal';
import { projectService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { CreateProjectRequest } from '../types';

// Extended project interface to include an ID and creation date
interface Project extends ProjectFormData {
  id: string;
  createdAt: Date;
  assetCounts?: {
    chargers: number;
    excavators: number;
    energyMeters: number;
  };
}

// Use environment variable for Mapbox access token
const MAPBOX_ACCESS_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

// Validate the access token format
const isValidMapboxToken = (token: string | undefined) => {
  return token && token.startsWith('pk.') && token.length > 50;
};

if (!MAPBOX_ACCESS_TOKEN || !isValidMapboxToken(MAPBOX_ACCESS_TOKEN)) {
  console.error('Invalid or missing Mapbox access token. Please set REACT_APP_MAPBOX_ACCESS_TOKEN in your environment variables.');
}

mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN || '';

// Project Map Component
interface ProjectMapProps {
  coordinates: {
    latitude: number;
    longitude: number;
  };
  projectName: string;
}

const ProjectMap: React.FC<ProjectMapProps> = ({ coordinates, projectName }) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);

  useEffect(() => {
    if (map.current) return; // initialize map only once
    if (!mapContainer.current) return;

    const initializeMap = () => {
      try {
        const container = mapContainer.current!;
        
        if (container.offsetWidth === 0 || container.offsetHeight === 0) {
          setTimeout(initializeMap, 100);
          return;
        }

        map.current = new mapboxgl.Map({
          container: container,
          style: 'mapbox://styles/mapbox/streets-v11',
          center: [coordinates.longitude, coordinates.latitude],
          zoom: 14
        });

        // Map is ready
        map.current.on('load', () => {
          console.log(`Map loaded for project: ${projectName}`);
        });

        map.current.on('error', (e) => {
          console.error('Map error:', e);
        });
      } catch (error) {
        console.error('Error initializing project map:', error);
      }
    };

    const timer = setTimeout(initializeMap, 100);

    return () => {
      clearTimeout(timer);
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [coordinates.latitude, coordinates.longitude, projectName]);

  return (
    <div 
      ref={mapContainer} 
      style={{ 
        width: '100%', 
        height: '100%',
        minHeight: '180px'
      }} 
    />
  );
};

const Projects: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { orgId } = useParams<{ orgId: string }>();
  const { user } = useAuth();
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreatingProject, setIsCreatingProject] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Function to fetch project info for a single project
  const fetchProjectInfo = async (projectId: string, orgId: string): Promise<{ chargers: number; excavators: number; energyMeters: number }> => {
    try {
      const response = await projectService.getProjectInfo(projectId, orgId);
      if (response.success && response.data) {
        return {
          chargers: response.data.chargers || 0,
          excavators: response.data.excavators || 0,
          energyMeters: response.data.energy_meter || 0,
        };
      }
    } catch (error) {
      console.error(`Error fetching project info for ${projectId}:`, error);
    }
    return { chargers: 0, excavators: 0, energyMeters: 0 };
  };

  // Fetch projects from API
  const fetchProjects = useCallback(async () => {
    if (!orgId) {
      console.error('Organization ID not found in URL');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log('Fetching projects for orgId:', orgId);
      const response = await projectService.getProjects(orgId);
      
      if (response.success && response.data) {
        console.log('Projects fetched successfully:', response.data);
        console.log('Response data type:', typeof response.data);
        console.log('Is array:', Array.isArray(response.data));
        console.log('Response structure:', JSON.stringify(response, null, 2));
        
        // Handle different possible response structures
        let projectsData: Project[] = [];
        
        if (Array.isArray(response.data)) {
          projectsData = response.data;
        } else if (response.data && typeof response.data === 'object' && 'projects' in response.data && Array.isArray((response.data as any).projects)) {
          // Transform the API response to match our Project interface
          projectsData = (response.data as any).projects.map((apiProject: any) => ({
            id: apiProject.project_id,
            name: apiProject.project_name,
            startDate: apiProject.start_date || new Date().toISOString().split('T')[0],
            endDate: apiProject.end_date || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            address: apiProject.address || 'Address not provided',
            units: '1-25', // Default units (not provided by API)
            createdAt: new Date(),
            coordinates: {
              latitude: apiProject.lat,
              longitude: apiProject.lng
            }
          }));
        } else if (response.data && typeof response.data === 'object' && 'data' in response.data && Array.isArray((response.data as any).data)) {
          projectsData = (response.data as any).data;
        } else {
          console.warn('Unexpected response structure:', response.data);
          projectsData = [];
        }
        
        // Fetch asset counts for each project
        const projectsWithAssetCounts = await Promise.all(
          projectsData.map(async (project) => {
            const assetCounts = await fetchProjectInfo(project.id, orgId);
            return {
              ...project,
              assetCounts
            };
          })
        );
        
        setProjects(projectsWithAssetCounts);
      } else {
        console.error('Failed to fetch projects:', response.message);
        setSnackbar({
          open: true,
          message: response.message || 'Failed to fetch projects',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      setProjects([]); // Ensure projects is always an array
      setSnackbar({
        open: true,
        message: 'Error fetching projects. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    fetchProjects();
  }, [orgId, fetchProjects]);

  useEffect(() => {
    if (map.current) return; // initialize map only once
    if (!mapContainer.current) return;

    // Function to initialize the map
    const initializeMap = () => {
      try {
        console.log('Initializing map in Projects...');
        const container = mapContainer.current!;
        console.log('Container dimensions:', container.offsetWidth, 'x', container.offsetHeight);
        
        // Ensure container has dimensions
        if (container.offsetWidth === 0 || container.offsetHeight === 0) {
          console.log('Container has no dimensions, retrying...');
          setTimeout(initializeMap, 500);
          return;
        }

        map.current = new mapboxgl.Map({
          container: container,
          style: 'mapbox://styles/mapbox/streets-v11',
          center: [-70.9, 42.35],
          zoom: 9
        });

        map.current.on('load', () => {
          console.log('Map loaded successfully in Projects');
        });

        map.current.on('error', (e) => {
          console.error('Map error in Projects:', e);
        });
        
        console.log('Map initialization complete in Projects');
      } catch (error) {
        console.error('Error initializing map in Projects:', error);
      }
    };

    // Start initialization with a delay to ensure DOM is ready
    const timer = setTimeout(initializeMap, 100);

    return () => {
      clearTimeout(timer);
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);



  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleSubmitProject = async (projectData: ProjectFormData) => {
    if (!user?.orgId) {
      setSnackbar({
        open: true,
        message: 'Organization ID not found. Please log in again.',
        severity: 'error'
      });
      return;
    }

    // Validate required fields
    if (!projectData.name || !projectData.startDate || !projectData.endDate || !projectData.address) {
      setSnackbar({
        open: true,
        message: 'Please fill in all required fields.',
        severity: 'error'
      });
      return;
    }

    // Validate coordinates
    if (!projectData.coordinates?.latitude || !projectData.coordinates?.longitude) {
      setSnackbar({
        open: true,
        message: 'Please select a valid address with coordinates.',
        severity: 'error'
      });
      return;
    }

    setIsCreatingProject(true);
    
    try {
      // Prepare the API request data
      const createProjectRequest: CreateProjectRequest = {
        org_id: user.orgId,
        project_name: projectData.name,
        lat: projectData.coordinates.latitude,
        lng: projectData.coordinates.longitude,
        start_date: new Date(projectData.startDate).toISOString().split('T')[0], // Convert to YYYY-MM-DD format
        end_date: new Date(projectData.endDate).toISOString().split('T')[0], // Convert to YYYY-MM-DD format
        address: projectData.address,
      };

      console.log('Creating project with data:', createProjectRequest);

      // Call the API to create the project
      const response = await projectService.createProject(createProjectRequest);
      
      if (response.success && response.data) {
        // Show success message
        setSnackbar({
          open: true,
          message: `Project "${projectData.name}" created successfully!`,
          severity: 'success'
        });
        
        // Close the modal
        setIsModalOpen(false);
        
        // Fetch all projects for the organization to get the updated list
        await fetchProjects();
      } else {
        throw new Error(response.message || 'Failed to create project');
      }
    } catch (error) {
      console.error('Error creating project:', error);
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : 'Failed to create project. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsCreatingProject(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleProjectClick = (projectId: string) => {
    navigate(`/projects/${projectId}`);
  };

  return (
    <Box sx={{ flexGrow: 1, backgroundColor: '#FAFAFA', px: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: '24px' }}>
        <Typography variant="h3">
          Projects
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenModal}
          sx={{
            backgroundColor: (theme) => theme.palette.primary.light,
            '&:hover': {
              backgroundColor: (theme) => theme.palette.primary.main,
              opacity: 0.9,
            },
            '& .MuiButton-label': {
              color: '#FFF',
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '13px',
              fontStyle: 'normal',
              fontWeight: 500,
              lineHeight: '22px',
              letterSpacing: '0.46px',
              textTransform: 'uppercase',
            },
            '& .MuiTypography-root': {
              color: '#FFF',
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '13px',
              fontStyle: 'normal',
              fontWeight: 500,
              lineHeight: '22px',
              letterSpacing: '0.46px',
              textTransform: 'uppercase',
            },
          }}
        >
          NEW PROJECT
        </Button>
      </Box>
      
      {loading ? (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          minHeight: '300px',
          textAlign: 'center'
        }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2, color: theme.palette.text.secondary }}>
            Loading projects...
          </Typography>
        </Box>
      ) : !Array.isArray(projects) || projects.length === 0 ? (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          minHeight: '300px',
          textAlign: 'center'
        }}>
          <Typography variant="h5" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
            No projects found
          </Typography>
          <Typography variant="body1" sx={{ color: '#999', mb: 3 }}>
            Create your first project to get started
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenModal}
            sx={{
              backgroundColor: (theme) => theme.palette.primary.light,
              '&:hover': {
                backgroundColor: (theme) => theme.palette.primary.main,
                opacity: 0.9,
              },
            }}
          >
            Create Project
          </Button>
        </Box>
      ) : (
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', 
          gap: '24px',
          mb: 3.75 
        }}>
          {Array.isArray(projects) && projects.map((project) => (
          <Card 
            key={project.id}
            onClick={() => handleProjectClick(project.id)}
            sx={{ 
              width: '100%',
              height: '320px',
              borderRadius: '8px',
              overflow: 'hidden',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
              cursor: 'pointer',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
              }
            }}
          >
            <CardContent sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
              {/* Map Background Section */}
              <Box sx={{ 
                height: '180px',
                position: 'relative',
                overflow: 'hidden',
                backgroundColor: '#f0f0f0',
                flexShrink: 0
              }}>
                {project.coordinates ? (
                  <ProjectMap 
                    coordinates={project.coordinates}
                    projectName={project.name}
                  />
                ) : (
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    color: theme.palette.text.secondary,
                    fontSize: '14px'
                  }}>
                    No map available
                  </Box>
                )}
                {/* Mapbox attribution */}
                <Box sx={{
                  position: 'absolute',
                  bottom: 8,
                  left: 8,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: theme.palette.text.secondary,
                  fontSize: '12px',
                  zIndex: 1
                }}>
                  <Box sx={{ width: 16, height: 16, bgcolor: theme.palette.text.secondary, borderRadius: '50%' }} />
                  mapbox
                </Box>
              </Box>

              {/* Asset Indicators Section */}
              <Box sx={{ 
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-around',
                px: 2,
                borderBottom: '1px solid #e0e0e0',
                backgroundColor: '#f8f9fa',
                flexShrink: 0
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <img src={process.env.PUBLIC_URL + '/Chargers.svg'} alt="Chargers" style={{ width: '16px', height: '16px', filter: 'brightness(0) saturate(100%) invert(40%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(0.4) contrast(1)' }} />
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                    {project.assetCounts?.chargers || 0}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <img src={process.env.PUBLIC_URL + '/Energy Meters.svg'} alt="Energy Meters" style={{ width: '16px', height: '16px', filter: 'brightness(0) saturate(100%) invert(40%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(0.4) contrast(1)' }} />
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                    {project.assetCounts?.energyMeters || 0}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <img src={process.env.PUBLIC_URL + '/DiggerIcon.svg'} alt="Excavators" style={{ width: '16px', height: '16px', filter: 'brightness(0) saturate(100%) invert(40%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(0.4) contrast(1)' }} />
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                    {project.assetCounts?.excavators || 0}
                  </Typography>
                </Box>
              </Box>

                             {/* Project Details Section */}
               <Box sx={{ 
                 minHeight: '80px',
                 display: 'flex',
                 flexDirection: 'column',
                 justifyContent: 'flex-start',
                 px: 2,
                 py: 1.5,
                 flex: 1
               }}>
                 <Typography variant="h6" sx={{ 
                   fontWeight: 600, 
                   color: theme.palette.text.primary,
                   mb: 0.5,
                   overflow: 'hidden',
                   textOverflow: 'ellipsis',
                   whiteSpace: 'nowrap',
                   fontSize: '1rem',
                   lineHeight: 1.2
                 }}>
                   {project.name}
                 </Typography>
                 <Typography variant="body2" sx={{ 
                   color: theme.palette.text.secondary,
                   fontWeight: 500,
                   mb: 0.5,
                   fontSize: '0.875rem',
                   lineHeight: 1.2
                 }}>
                   {new Date(project.startDate).toLocaleDateString()} - {new Date(project.endDate).toLocaleDateString()}
                 </Typography>
                 <Typography variant="caption" sx={{ 
                   color: '#999',
                   fontStyle: 'italic',
                   fontSize: '0.75rem',
                   lineHeight: 1.2,
                   overflow: 'hidden',
                   textOverflow: 'ellipsis',
                   whiteSpace: 'nowrap'
                 }}>
                   {project.address}
                 </Typography>
               </Box>
            </CardContent>
          </Card>
        ))}
        </Box>
      )}

      {/* New Project Modal */}
      <NewProjectModal
        open={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleSubmitProject}
        isLoading={isCreatingProject}
      />

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Projects;
